import { getVisualConfigs } from "@portal/api/account";
import {
  getCurrentUserConfig,
  getCustomerDefault,
} from "@portal/api/user-config";
import { logRocket } from "@portal/lib/error-monitor/logrocket";
import { errorMonitor } from "@portal/lib/error-monitor/sentry/errorMonitor";
import { AppPageParams } from "@portal/lib/router/constants/page-info";
import { usePageNavigate } from "@portal/lib/router/hooks/usePageNavigae";
import { authAtom } from "@portal/store/auth/auth";
import { userConfigAtom } from "@portal/store/userConfig/userConfig";
import { sessionStore } from "@portal/util/browser-storage";
import { useQuery } from "@tanstack/react-query";
import { useAtom, useSetAtom } from "jotai";
import { useLocation, useParams } from "react-router-dom";

function useUserConfigQuery() {
  const setUserConfig = useSetAtom(userConfigAtom);
  const [auth, setAuth] = useAtom(authAtom);
  const id = sessionStore.getItem("customer_id");
  const navigate = usePageNavigate();
  const location = useLocation();
  return useQuery({
    queryKey: ["user"],
    queryFn: () => getCurrentUserConfig(),
    enabled: id || auth.refetch ? true : false,
    async onSuccess(data) {
      setAuth({ authStatus: "authenticated" });
      if (data && Boolean(Object.keys(data).length)) {
        setUserConfig((value) => {
          return { ...value, user_details: data };
        });
        logRocket.identify(`${data.user.first_name} ${data.user.last_name} `, {
          Email: data.user.email,
          Customer: data.customer.name,
          "Portal Name": data.portal.name,
          "Portal Display Name": data.portal.display_name,
          "CUSTOM DOMAIN": data.portal.custom_domain ?? "",
        });
        errorMonitor.identify({
          Email: data.user.email,
          Customer: data.customer.name,
          "Portal Name": data.portal.name,
          "Portal Display Name": data.portal.display_name,
          "CUSTOM DOMAIN": data.portal.custom_domain,
        });
      }
      const queryParams = new URLSearchParams(location.search);
      queryParams.delete("customer_id");
      if (auth.refetch) {
        navigate({
          appPage: "INVOICES",
          pathParams: {
            customer_id: id as string,
          },
          searchParams: queryParams.has("entity_type")
            ? { ...queryParams }
            : undefined,
        });
      }
    },
    onError(err) {
      
      setAuth({ authStatus: "not-authenticated" });
      throw new Error(`${err}`);
    },
  });
}
function useUserVisualConfigQuery() {
  const setUserConfig = useSetAtom(userConfigAtom);

  return useQuery({
    queryKey: ["visual-config"],
    queryFn: () => getVisualConfigs(),
    async onSuccess(data) {
      if (data && Boolean(Object.keys(data).length)) {
        setUserConfig((value) => {
          return {
            ...value,
            visualConfig: {
              loading: false,
              config: data,
            },
          };
        });
      }
    },
    onError(err) {
      throw new Error(`${err}`);
    },
  });
}

function useCustomerDefaultQuery(onError: () => void) {
  const id = sessionStore.getItem("customer_id");
  const [, setAuth] = useAtom(authAtom);
  const { customer_id } = useParams<AppPageParams>();
  return useQuery({
    queryKey: ["customer-details"],
    queryFn: () => getCustomerDefault(),
    enabled: customer_id || id ? false : true,
    async onSuccess(data) {
      const path = data.url.split("/").pop();
      if (!path) return;
      sessionStore.setItem("customer_id", path);
      setAuth((value) => {
        return { ...value, refetch: true };
      });
    },
    onError(err) {
      onError();
      throw new Error(`${err}`);
    },
  });
}

export {
  useCustomerDefaultQuery,
  useUserConfigQuery,
  useUserVisualConfigQuery,
};
